/**
 * Shared table include checklist for daily charts (Uptime by day, Errors / Warnings by day,
 * Stints / uptime hour, Revenue, Stints to payment confirmed). Persisted as `tables` on every
 * dashboard tab so setting it in one place updates the others. It is not the dashboard Device
 * filter.
 *
 * Omitted / `all` = every table in scope. `none` = none. Otherwise comma-separated device ids.
 */
const DEVICE_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MAX_TABLES = 200;

export type ChartTableSelection = "all" | "none" | string[];

export interface ChartTableDevice {
  id: string;
  name: string;
  venueName?: string;
}

export const SHARED_TABLES_CRITERIA = `**Tables** dropdown (same checklist as Health **Uptime**): persisted in the URL as \`tables\` (omitted = all tables in the current venue/device scope, \`none\`, or comma-separated device ids). Setting it on any of Uptime, Errors / Warnings by day, Interaction Stints / Uptime Hour, Revenue, Revenue per table, or Stints to payment confirmed updates the others. It does **not** change the dashboard Device filter. When that filter is a single table, it locks the chart and the dropdown is hidden. Default (all selected) uses the fleet series; a subset sums those tables' days only.`;

export const SHARED_FULL_WEEKS_CRITERIA = `**Full weeks** (off by default): persisted in the URL as \`weeks=1\` (omit = days). Checking it on Uptime, Errors / Warnings by day, Interaction Stints / Uptime Hour, Revenue, Revenue per table, or Stints to payment confirmed updates the others. Overview PDF follows the same flag.`;

export function parseChartTablesParam(raw: string | null): ChartTableSelection {
  if (raw == null || raw.trim() === "" || raw === "all") return "all";
  if (raw === "none") return "none";
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const part of raw.split(",")) {
    const id = part.trim().toLowerCase();
    if (!DEVICE_ID_RE.test(id) || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
    if (ids.length >= MAX_TABLES) break;
  }
  return ids.length === 0 ? "all" : ids;
}

export function chartTablesParamValue(selection: ChartTableSelection): string | null {
  if (selection === "all") return null;
  if (selection === "none") return "none";
  return selection.length === 0 ? "none" : selection.join(",");
}

export function chartTablesEqual(a: ChartTableSelection, b: ChartTableSelection): boolean {
  if (a === "all" || a === "none" || b === "all" || b === "none") return a === b;
  if (a.length !== b.length) return false;
  return a.every((id, i) => id === b[i]);
}

export function selectionToIds(
  selection: ChartTableSelection,
  deviceIds: readonly string[],
): Set<string> {
  if (selection === "all") return new Set(deviceIds);
  if (selection === "none") return new Set();
  if (deviceIds.length === 0) return new Set(selection);
  const wanted = new Set(selection.map((id) => id.toLowerCase()));
  return new Set(deviceIds.filter((id) => wanted.has(id.toLowerCase())));
}

export function idsToSelection(
  selected: ReadonlySet<string>,
  deviceIds: readonly string[],
): ChartTableSelection {
  if (deviceIds.length === 0) {
    return selected.size === 0 ? "none" : [...selected];
  }
  if (selected.size === 0) return "none";
  if (deviceIds.every((id) => selected.has(id))) return "all";
  return deviceIds.filter((id) => selected.has(id));
}

/**
 * Drop ids that left the current device list. An empty intersection resets to all (same as
 * Uptime by day when the last checked table disappears). `none` and `all` are kept. Does not
 * write while the device list is still empty — that would wipe a subset on first paint.
 */
export function reconcileChartTables(
  selection: ChartTableSelection,
  deviceIds: readonly string[],
): ChartTableSelection {
  if (deviceIds.length === 0 || selection === "all" || selection === "none") return selection;
  const known = new Set(deviceIds.map((id) => id.toLowerCase()));
  const kept = selection.filter((id) => known.has(id.toLowerCase()));
  if (kept.length === 0 || kept.length === deviceIds.length) return "all";
  const canonical = deviceIds.filter((id) =>
    kept.some((k) => k.toLowerCase() === id.toLowerCase()),
  );
  if (
    selection.length === canonical.length &&
    selection.every((id, i) => id === canonical[i])
  ) {
    return selection;
  }
  return canonical;
}
